import { Box } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import SaveRecipeDialog from '@/components/SaveRecipeDialog'
import NewKonvaCanvas from '@/components/canvas/NewKonvaCanvas'
import ImageEdit from '@/components/imageEdit/ImageEdit'
import { useAppSelector } from '@/hooks/redux'
import { useCanvasSize } from '@/hooks/useCanvasSize'
import useGenerateImage from '@/hooks/useGenerateImage'
import CaseInfoLayout from '@/layout/CaseInfoLayout'
import { imageType } from '@/pages/getRecipe/allInOne/utils/constants'
import {
  useGenerateGingivaStainingRecipeMutation,
  useSavePdfMutation,
  useSaveRecipeMutation,
} from '@/store/apiService'
import type { LABColor } from '@/store/appSlice'
import {
  ImageCalibrationStatus,
  setImageCalibrationStatus,
} from '@/store/sessionSlice'
import {
  clearGingivaStainingState,
  setGingivaRecipeOutput,
  setGingivaStainingTarget,
  setSelectedGingivaStainingKey,
} from '@/store/stainingStudio'
import { getCaseId } from '@/utils'
import { Workflows } from '@/utils/constants'
import {
  getDehazedImageUrl,
  hasNonEmptyOutputSingleCanvas,
  saveRecipeGeneric,
} from '@/utils/helper'

import GingivaStainingPanel from './panel/GingivaStainingPanel'

const GingivaStaining = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'gingiva' })

  const dispatch = useDispatch()
  const location = useLocation()
  const combinedRef = useRef<HTMLDivElement>(null)

  const { generateImage } = useGenerateImage()
  const caseId = getCaseId() || ''

  const [saveRecipedialogOpen, setSaveRecipedialogOpen] = useState(false)
  const [recipeName, setRecipeName] = useState('')

  const prevPathRef = useRef(location.pathname)
  const canvasImage = useAppSelector((state) => state.app.canvasImage)
  const imageCalibrationStatus = useAppSelector(
    (state) => state.session.imageCalibrationStatus,
  )
  const { containerRef, dimensions } = useCanvasSize()

  const [generateGingivaRecipe] = useGenerateGingivaStainingRecipeMutation()
  const [saveRecipe, { isLoading }] = useSaveRecipeMutation()
  const [savePdf] = useSavePdfMutation()

  const gingivaStainingBrand = useAppSelector(
    (state) => state.stainingStudio.gingivastainingbrand,
  )

  const selectedGingivaStainingKey = useAppSelector(
    (state) => state.stainingStudio.selectedGingivaStainingKey,
  )

  const gingivaRecipeOutput = useAppSelector(
    (state) => state.stainingStudio.gingivaRecipeOutput,
  )

  const path = useLocation().pathname
  const imageEdit = useAppSelector(
    (state) => state.app.imageEdit[path] || false,
  )

  const gingivaStainingTargets = useAppSelector(
    (state) => state.stainingStudio.gingivaStaing,
  )

  useEffect(() => {
    // Only clear state if we're returning from a different route
    if (prevPathRef.current !== location.pathname) {
      dispatch(clearGingivaStainingState())
    }
    prevPathRef.current = location.pathname
  }, [dispatch, location.pathname])

  const onImageClick = (
    _type: string,
    _placement: string,
    imagePos: { x: number; y: number },
    _hiddenCanvasRef: any,
    labColor: LABColor,
  ) => {
    if (
      canvasImage?.id &&
      imageCalibrationStatus?.[canvasImage?.id] ===
        ImageCalibrationStatus.CALIBRATING &&
      _type === imageType.RAW
    ) {
      if (canvasImage?.id) {
        dispatch(
          setImageCalibrationStatus({
            id: canvasImage?.id,
            status: ImageCalibrationStatus.CALIBRATED,
          }),
        )
      }
    }

    if (selectedGingivaStainingKey) {
      if (!gingivaStainingBrand) {
        toast.error(t('selectStainingBrand'))
        return
      }
      // Set position, target color, and output in a single dispatch
      dispatch(
        setGingivaStainingTarget({
          imageId: canvasImage?.id,
          targetcolor: [labColor.L, labColor.a, labColor.b],
          position: imagePos,
        }),
      )

      dispatch(
        setGingivaStainingTarget({
          imageId: canvasImage?.id,
          output: {
            [selectedGingivaStainingKey]: selectedGingivaStainingKey,
          },
        }),
      )

      const targetcolor: [number, number, number] = [
        Number(labColor.L),
        Number(labColor.a),
        Number(labColor.b),
      ]

      generateGingivaRecipe({
        gingivastainingbrand: gingivaStainingBrand,
        targetcolor,
      })
        .unwrap()
        .then((res) => {
          toast.success(t('recipeGenerated'))

          dispatch(
            setGingivaRecipeOutput({
              imageId: canvasImage?.id,
              key: selectedGingivaStainingKey,
              result: res,
            }),
          )
        })
        .catch((error) => {
          toast.error(error?.data?.message || t('recipeGenerationFailed'))
        })

      // Clear the selected key immediately
      dispatch(setSelectedGingivaStainingKey(''))
    }
  }

  const regenerateRecipe = (regionKey: string, labColor: LABColor) => {
    if (!gingivaStainingBrand) {
      toast.error(t('selectStainingBrand'))
      return
    }

    if (!canvasImage?.id) return

    const currentTarget =
      gingivaStainingTargets?.[canvasImage.id]?.[regionKey]?.targetcolor
    const newTarget = labColor[regionKey]

    if (
      !currentTarget ||
      currentTarget[0] !== newTarget[0] ||
      currentTarget[1] !== newTarget[1] ||
      currentTarget[2] !== newTarget[2]
    ) {
      dispatch(
        setGingivaStainingTarget({
          key: regionKey,
          imageId: canvasImage.id,
          targetcolor: newTarget,
          position:
            gingivaStainingTargets?.[canvasImage.id]?.[regionKey]?.position,
          output: {
            [regionKey]: regionKey,
          },
        }),
      )
    }

    generateGingivaRecipe({
      gingivastainingbrand: gingivaStainingBrand,
      targetcolor: newTarget,
    })
      .unwrap()
      .then((res) => {
        toast.success(t('recipeGenerated'))

        dispatch(
          setGingivaRecipeOutput({
            imageId: canvasImage.id,
            key: regionKey,
            result: res,
          }),
        )
      })
      .catch((error) => {
        toast.error(error?.data?.message || t('recipeGenerationFailed'))
      })
  }

  const onSaveAsImage = () => {
    generateImage(combinedRef, 'Gingiva-Recipe.png')
  }

  const handleOpenSaveDialog = () => {
    setSaveRecipedialogOpen(true)
  }

  const handleSaveToProject = async (targetCaseId: string) => {
    await saveRecipeGeneric({
      caseId: targetCaseId,
      canvasImage,
      recipeName,
      output: gingivaRecipeOutput[canvasImage?.id],
      workflow: Workflows.STAINING_STUDIO,
      mutationFn: saveRecipe,
      closeDialog: () => setSaveRecipedialogOpen(false),
      recipeType: 'staining_gingiva',
    })
  }

  const onSaveAsPDF = async () => {
    await saveRecipeGeneric({
      caseId,
      canvasImage,
      recipeName,
      output: gingivaRecipeOutput[canvasImage?.id],
      workflow: Workflows.STAINING_STUDIO,
      mutationFn: savePdf,
      recipeType: 'staining_gingiva',
      isPdf: true,
    })
  }
  return (
    <CaseInfoLayout
      rightPanel={
        <GingivaStainingPanel
          containerRef={containerRef}
          regenerateRecipe={regenerateRecipe}
        />
      }
      disabled={hasNonEmptyOutputSingleCanvas(
        gingivaRecipeOutput,
        canvasImage?.id,
      )}
      onSaveAsImage={onSaveAsImage}
      onSaveAsPDF={onSaveAsPDF}
      handleSaveToProject={handleOpenSaveDialog}
    >
      <Box display="flex" flexDirection="column" flexGrow={1}>
        <Box
          ref={containerRef}
          display="flex"
          flexGrow={1}
          flexDirection={'column'}
        >
          {!imageEdit ? (
            <>
              <NewKonvaCanvas
                type={canvasImage?.type}
                imageUrl={
                  getDehazedImageUrl(canvasImage) ||
                  canvasImage?.latest_image ||
                  canvasImage?.calibrated_image ||
                  canvasImage?.image
                }
                width={dimensions.width}
                height={dimensions.height}
                onImageClick={onImageClick}
                imageId={canvasImage?.id}
                combinedRef={combinedRef}
                rawWidth={canvasImage?.raw_width}
                rawHeight={canvasImage?.raw_height}
                isActiveCircle={!!selectedGingivaStainingKey}
              />
            </>
          ) : (
            <ImageEdit selectedImage={canvasImage} />
          )}
        </Box>
      </Box>
      <SaveRecipeDialog
        saveRecipedialogOpen={saveRecipedialogOpen}
        setSaveRecipedialogOpen={setSaveRecipedialogOpen}
        recipeName={recipeName}
        setRecipeName={setRecipeName}
        onSave={handleSaveToProject}
        isLoading={isLoading}
      />
    </CaseInfoLayout>
  )
}

export default GingivaStaining
